import { useEffect, useState } from "react";
import { Alert, Modal } from "react-bootstrap";
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiRefreshCw, FiTag, FiGift, FiMapPin, FiFileText, FiClock, FiMessageCircle, FiCalendar } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import apiClient from "../../api/ApiClients";
import Sidebar from "../../components/layout/Sidebar";
import { getDistribution, formatSubmitted, istDateBoundary } from "./feedbackPresentation";
import "./MaternityFeedbackPage.css";

const answerStyle = {
  price_high: { icon: FiTag, color: "#c39a8a", tone: "price" },
  different_package: { icon: FiGift, color: "#d6b390", tone: "package" },
  location_details: { icon: FiMapPin, color: "#a7c3b5", tone: "location" },
  inclusions_unclear: { icon: FiFileText, color: "#adc5db", tone: "included" },
  comparing: { icon: FiClock, color: "#c2b0d8", tone: "comparing" },
  other: { icon: FiMessageCircle, color: "#c58f94", tone: "other" },
};
function AnswerIcon({ value }) {
  const style = answerStyle[value] || answerStyle.other;
  const Icon = style.icon;
  return <span className={`mf-answer-icon mf-${style.tone}`}><Icon aria-hidden="true" /></span>;
}
function Distribution({ answers, total }) {
  const distribution = getDistribution(answers, total);
  return <section className="mf-panel mf-total-panel" aria-labelledby="mf-total-title">
    <div className="mf-total-copy"><h2 id="mf-total-title">Total responses</h2><strong>{total.toLocaleString()}</strong><p>in selected period</p></div>
    <div className="mf-donut" role="img" aria-label={`${total} ${total === 1 ? "response" : "responses"}. ${distribution.filter(a => a.count).map(a => `${a.label}: ${a.count}, ${a.percentage}%`).join("; ") || "No responses in selected period."}`}>
      <svg viewBox="0 0 200 200" aria-hidden="true">
        <circle cx="100" cy="100" r="76" fill="none" stroke="#f1eceb" strokeWidth="30" />
        {distribution.filter(a => a.count).map(a => <circle key={a.value} cx="100" cy="100" r="76" fill="none" stroke={answerStyle[a.value]?.color || answerStyle.other.color} strokeWidth="30" pathLength="100" strokeDasharray={`${a.share} ${100 - a.share}`} strokeDashoffset={-a.offset} transform="rotate(-90 100 100)" />)}
      </svg>
      <div className="mf-donut-center"><strong>{total.toLocaleString()}</strong><span>{total === 1 ? "Response" : "Responses"}</span></div>
    </div>
    <ul className="mf-legend">{distribution.some(a => a.count) ? distribution.filter(a => a.count).map(a => <li key={a.value}><span className="mf-legend-dot" style={{ background: answerStyle[a.value]?.color || answerStyle.other.color }} /><span>{a.label}</span><span>{a.count}</span><strong>{a.percentage}%</strong></li>) : <li className="mf-empty">No responses in this period.</li>}</ul>
  </section>;
}
export default function MaternityFeedbackPage() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [selection, setSelection] = useState("");
  const [dates, setDates] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [detail, setDetail] = useState(null);
  const limit = 25;
  useEffect(() => {
    let active = true;
    apiClient.request("survey/config").then(data => {
      if (!active) return;
      if (!data?.surveys?.length) { setError(true); setLoading(false); return; }
      setSurveys(data.surveys);
      setSelection(current => current === "" ? String(data.surveys.length - 1) : current);
    });
    return () => { active = false; };
  }, [refresh]);
  useEffect(() => {
    const config = surveys[Number(selection)];
    if (selection === "" || !config) return;
    let active = true;
    setLoading(true); setError(false); setDetail(null);
    const query = new URLSearchParams({ surveyId: config.surveyId, version: config.version, serviceSlug: config.serviceSlug, page: String(page), limit: String(limit) });
    if (dates.from) query.set("from", istDateBoundary(dates.from));
    if (dates.to) query.set("to", istDateBoundary(dates.to, true));
    apiClient.request(`survey/results?${query}`).then(data => {
      if (!active) return;
      setResult(data?.error ? null : data || null);
      setError(!data || data.error); setLoading(false);
    });
    return () => { active = false; };
  }, [surveys, selection, dates, page, refresh]);
  const changeDate = event => { setPage(1); setDates(current => ({ ...current, [event.target.name]: event.target.value })); };
  const distribution = result ? getDistribution(result.answers, result.totalResponses) : [];
  const first = result?.responses.length ? (page - 1) * limit + 1 : 0;
  const last = result?.responses.length ? first + result.responses.length - 1 : 0;
  return <div className="mf-shell">
    <Sidebar />
    <main className="mf-page">
      <header className="mf-header">
        <button type="button" className="mf-back" aria-label="Back to dashboard" onClick={() => navigate("/admin/dashboard")}><FiArrowLeft aria-hidden="true" /></button>
        <div><h1>Maternity Feedback</h1><p>Understand why customers didn’t book, so we can create a better experience.</p></div>
        <img className="mf-decoration" src="/assets/maternity-feedback.svg" alt="" aria-hidden="true" width="260" height="240" />
      </header>
      <section className="mf-panel mf-filters" aria-label="Feedback filters">
        <div className="mf-filter-survey"><label htmlFor="feedback-survey">Survey / version / service</label>
          <select id="feedback-survey" value={selection} onChange={event => { setSelection(event.target.value); setPage(1); }}>
            {surveys.map((s, i) => <option key={`${s.surveyId}:${s.version}:${s.serviceSlug}`} value={i}>{s.surveyId} · v{s.version} · {s.serviceSlug}</option>)}
          </select>
        </div>
        <div><label htmlFor="feedback-from">From (IST)</label><input id="feedback-from" type="date" name="from" value={dates.from} onChange={changeDate} max={dates.to || undefined} /></div>
        <div><label htmlFor="feedback-to">To (IST)</label><input id="feedback-to" type="date" name="to" value={dates.to} onChange={changeDate} min={dates.from || undefined} /></div>
        <button type="button" className="mf-refresh" onClick={() => setRefresh(n => n + 1)} disabled={loading}><FiRefreshCw aria-hidden="true" />Refresh</button>
      </section>
      {loading && <p className="mf-loading" role="status">Loading feedback…</p>}
      {error && <Alert variant="danger">Unable to load feedback. Please check the filters and try Refresh.</Alert>}
      {!loading && !error && result && <>
        <div className="mf-summary">
          <Distribution answers={result.answers} total={result.totalResponses} />
          <section className="mf-panel mf-breakdown" aria-labelledby="mf-breakdown-title">
            <h2 id="mf-breakdown-title">Why they didn’t book?</h2>
            <div className="mf-breakdown-head"><span /><span>Count</span><span>Percentage</span></div>
            <ul aria-label="Feedback answer summary">{distribution.map(a => <li key={a.value}>
              <div className="mf-answer-label"><AnswerIcon value={a.value} /><span>{a.label}</span></div>
              <div className="mf-progress" aria-hidden="true"><span style={{ width: `${a.share}%`, background: answerStyle[a.value]?.color || answerStyle.other.color }} /></div>
              <span className="mf-count">{a.count}</span><span className={`mf-percentage ${a.count ? "mf-nonzero" : ""}`}>{a.percentage}%</span>
            </li>)}</ul>
          </section>
        </div>
        <section className="mf-panel mf-responses" aria-labelledby="mf-responses-title">
          <h2 id="mf-responses-title">Responses ({result.totalResponses.toLocaleString()})</h2>
          <p>Individual feedback responses from customers.</p>
          <div className="mf-table-scroll" tabIndex={0} role="region" aria-label="Scrollable feedback responses">
            <table aria-label="Feedback responses"><thead><tr><th>#</th><th>Answer</th><th>Feedback</th><th>Survey / version</th><th>Service</th><th>Submitted</th></tr></thead>
              <tbody>{result.responses.length ? result.responses.map((r, index) => <tr key={`${page}:${index}`}>
                <td>{first + index}</td>
                <td><span className={`mf-badge mf-${answerStyle[r.answer]?.tone || "other"}`}><AnswerIcon value={r.answer} />{result.answers.find(a => a.value === r.answer)?.label || r.answer}</span></td>
                <td>{r.feedback ? <button type="button" className="mf-feedback-preview" onClick={() => setDetail(r.feedback)} aria-label={`Read full feedback for response ${first + index}`}><span>{r.feedback}</span><small>Read feedback</small></button> : <span className="mf-empty">—</span>}</td>
                <td className="mf-context">{r.surveyId} / v{r.version}</td><td className="mf-context">{r.serviceSlug}</td>
                <td><div className="mf-submitted"><FiCalendar aria-hidden="true" /><div><time dateTime={r.createdAt}>{formatSubmitted(r.createdAt)}</time><small>(IST)</small></div></div></td>
              </tr>) : <tr><td colSpan={6} className="mf-empty">No responses in this period.</td></tr>}</tbody>
            </table>
          </div>
          <footer className="mf-pagination"><span>Showing {first}{last !== first ? `–${last}` : ""} of {result.totalResponses.toLocaleString()} {result.totalResponses === 1 ? "response" : "responses"}</span>
            <div><button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><FiChevronLeft aria-hidden="true" /></button><span className="mf-current-page" aria-label={`Page ${page} of ${result.totalPages}`}>{page}</span><button type="button" aria-label="Next page" disabled={page >= result.totalPages} onClick={() => setPage(p => p + 1)}><FiChevronRight aria-hidden="true" /></button></div>
          </footer>
        </section>
      </>}
      <Modal show={detail !== null} onHide={() => setDetail(null)} centered contentClassName="mf-feedback-modal">
        <Modal.Header closeButton><Modal.Title>Written feedback</Modal.Title></Modal.Header>
        <Modal.Body><p className="mf-feedback-full">{detail}</p></Modal.Body>
      </Modal>
    </main>
    <ToastContainer />
  </div>;
}
